<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError && touched,
      'form-group--has-success': !localError && touched,
    }"
  >
    <div class="sf-input sf-input--select">
      <label :for="id">{{ label }}</label>
      <select :id="id" :value="modelValue" @change="onChange" @blur="onBlur">
        <option
          v-for="font in fonts"
          :key="font.name"
          :value="font.name"
          :style="{ fontFamily: font.name }"
        >
          {{ font.name }}
        </option>
      </select>
      <span v-if="localError && touched" class="form-group__error">{{ localError }}</span>
    </div>
  </div>
</template>

<script>
  import { ref, watch, onMounted } from 'vue';
  import { validateInput } from '@/utils/validation';

  // Pre-defined list of fonts with weights
  const FONTS = [
    { name: 'Inter', weights: [400, 500, 600, 700] },
    { name: 'Roboto', weights: [400, 500, 700] },
    { name: 'Open Sans', weights: [400, 600, 700] },
    { name: 'Lato', weights: [400, 700] },
    { name: 'Poppins', weights: [400, 500, 600, 700] },
    { name: 'Montserrat', weights: [400, 500, 600, 700] },
    { name: 'Source Sans Pro', weights: [400, 600, 700] },
    { name: 'Raleway', weights: [400, 500, 600, 700] },
    { name: 'Ubuntu', weights: [400, 500, 700] },
    { name: 'Playfair Display', weights: [400, 500, 600, 700] },
    { name: 'Merriweather', weights: [400, 700] },
    { name: 'Readex Pro', weights: [400, 500, 600, 700] },
    { name: 'Work Sans', weights: [400, 500, 600, 700] },
  ];

  export default {
    name: 'FontPicker',
    props: {
      id: {
        type: String,
        default: 'font-picker',
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: String,
        required: true,
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      realTimeValidation: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue', 'validate'],
    setup(props, { emit }) {
      const localError = ref(props.error);
      const touched = ref(false);
      const fonts = ref(FONTS);

      const loadFonts = () => {
        try {
          // Load font styles for all predefined fonts
          const fontFamilies = fonts.value
            .map((font) => {
              const weights = font.weights.join(',');
              return `${font.name}:${weights}`;
            })
            .join('|');

          const link = document.createElement('link');
          link.href = `https://fonts.googleapis.com/css?family=${fontFamilies}&display=swap`;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        } catch (error) {
          console.error('Error loading fonts:', error);
          localError.value = 'Failed to load fonts';
        }
      };

      onMounted(() => {
        loadFonts();
      });

      const onChange = (event) => {
        touched.value = true;
        const selectedFont = fonts.value.find((f) => f.name === event.target.value);
        emit('update:modelValue', selectedFont.name);
        if (props.realTimeValidation) {
          validate();
        }
      };

      const onBlur = () => {
        touched.value = true;
        validate();
      };

      const validate = () => {
        const errorMessage = validateInput(props.modelValue, props.rules);
        localError.value = errorMessage;
        emit('validate', errorMessage);
      };

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      return {
        onChange,
        onBlur,
        validate,
        localError,
        touched,
        fonts,
      };
    },
  };
</script>

<style scoped>
  .sf-input--select select {
    width: 100%;
    padding: 8px 12px;
    font-size: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background-color: white;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
  }

  .sf-input--select select:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .sf-input--select select option {
    padding: 12px;
    font-size: 16px;
    background-color: white;
    color: #1a1a1a;
  }

  .sf-input--select select option:hover {
    background-color: #f8fafc;
  }

  /* Add some spacing between label and select */
  .sf-input label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    color: #4a5568;
  }
</style>
